import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";
import TurnstileWidget from "./TurnstileWidget";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";

const teamSizes = ["Just me", "2–9 reps", "10–24 reps", "25–49 reps", "50+ reps"];

export default function DemoModal({ trigger, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    const [done, setDone] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [token, setToken] = useState("");
    const [form, setForm] = useState({
        name: "",
        work_email: "",
        company: "",
        role: "",
        phone: "",
        team_size: "",
        message: "",
    });

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.work_email || !form.company || !form.team_size) {
            toast.error("Please fill in name, email, company and team size.");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/demo-requests", { ...form, turnstile_token: token || undefined });
            setDone(true);
            toast.success("Request received — we'll be in touch within one business day.");
        } catch (err) {
            toast.error(err?.response?.data?.detail?.[0]?.msg || "Could not submit request. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    const reset = () => {
        setDone(false);
        setForm({ name: "", work_email: "", company: "", role: "", team_size: "", message: "" });
    };

    return (
        <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setTimeout(reset, 200); }}>
            <DialogTrigger asChild>
                {trigger}
            </DialogTrigger>
            <DialogContent data-testid="demo-modal" className="sm:max-w-lg">
                {!done ? (
                    <>
                        <DialogHeader>
                            <DialogTitle className="font-display text-2xl tracking-tight">Book a demo</DialogTitle>
                            <DialogDescription>
                                See PipeGuide live on a real call. We'll show you scoring, suggested questions, and the manager dashboard.
                            </DialogDescription>
                        </DialogHeader>

                        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="demo-form">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label htmlFor="d-name">Full name</Label>
                                    <Input id="d-name" data-testid="demo-name" value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Jane Smith" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="d-email">Work email</Label>
                                    <Input id="d-email" type="email" data-testid="demo-email" value={form.work_email} onChange={(e) => update("work_email", e.target.value)} placeholder="jane@company.com" />
                                </div>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div className="space-y-1.5">
                                    <Label htmlFor="d-company">Company</Label>
                                    <Input id="d-company" data-testid="demo-company" value={form.company} onChange={(e) => update("company", e.target.value)} placeholder="Acme Corp" />
                                </div>
                                <div className="space-y-1.5">
                                    <Label htmlFor="d-role">Your role</Label>
                                    <Input id="d-role" data-testid="demo-role" value={form.role} onChange={(e) => update("role", e.target.value)} placeholder="VP Sales" />
                                </div>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="d-phone">
                                    Contact number <span className="text-zinc-400 font-normal">· optional</span>
                                </Label>
                                <PhoneInput
                                    id="d-phone"
                                    data-testid="demo-phone"
                                    international
                                    defaultCountry="ZA"
                                    value={form.phone}
                                    onChange={(value) => update("phone", value || "")}
                                    placeholder="82 123 4567"
                                    className="pg-phone-input"
                                />
                                {form.phone && !isValidPhoneNumber(form.phone) && (
                                    <p className="text-xs text-amber-700">Please check the number — looks slightly off.</p>
                                )}
                            </div>
                            <div className="space-y-1.5">
                                <Label>Team size</Label>
                                <Select value={form.team_size} onValueChange={(v) => update("team_size", v)}>
                                    <SelectTrigger data-testid="demo-team-size"><SelectValue placeholder="How many sales reps?" /></SelectTrigger>
                                    <SelectContent>
                                        {teamSizes.map((s) => (<SelectItem key={s} value={s}>{s}</SelectItem>))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="space-y-1.5">
                                <Label htmlFor="d-msg">What would you like to see? (optional)</Label>
                                <Textarea id="d-msg" data-testid="demo-message" rows={3} value={form.message} onChange={(e) => update("message", e.target.value)} placeholder="We use HubSpot and run BANT discovery calls…" />
                            </div>
                            <TurnstileWidget onToken={setToken} />
                            <Button type="submit" disabled={submitting} data-testid="demo-submit-btn" className="w-full bg-brand-primary hover:bg-brand-hover text-white h-11">
                                {submitting ? "Sending…" : "Request a demo"}
                            </Button>
                            <p className="text-xs text-zinc-500 text-center">By submitting you agree to be contacted by PipeGuide. No credit card required.</p>
                        </form>
                    </>
                ) : (
                    <div data-testid="demo-success" className="py-6 text-center space-y-4">
                        <div className="mx-auto h-12 w-12 rounded-full bg-emerald-50 flex items-center justify-center">
                            <CheckCircle2 className="h-6 w-6 text-emerald-600" />
                        </div>
                        <DialogTitle className="font-display text-2xl">Thanks — request received</DialogTitle>
                        <p className="text-sm text-zinc-600">A member of our team will reach out within one business day to schedule your walkthrough.</p>
                        <Button onClick={() => setOpen(false)} variant="outline" data-testid="demo-close-btn">Close</Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
}
